import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import {
  FaRocket,
  FaSmile,
  FaAward,
  FaChartPie,
  FaStar,
  FaClock,
} from "react-icons/fa";
import "./StatsSection.css";

const CounterNumber = ({ target, suffix = "", duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (!isInView) return;

    let start = 0;
    const end = parseFloat(target);
    const isDecimal = target.includes(".");
    const totalFrames = Math.round(duration * 60);
    let frame = 0;

    const timer = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;
      // Ease out quad
      const current = start + (end - start) * (1 - Math.pow(1 - progress, 3));

      if (frame >= totalFrames) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(isDecimal ? parseFloat(current.toFixed(1)) : Math.floor(current));
      }
    }, 1000 / 60);

    return () => clearInterval(timer);
  }, [isInView, target, duration]);

  return (
    <span ref={ref} className="stat-number">
      {count}
      {suffix}
    </span>
  );
};

const statsData = [
  {
    icon: FaRocket,
    value: "150",
    suffix: "+",
    label: "Successful Projects",
    desc: "Websites, apps & campaigns engineered to scale",
  },
  {
    icon: FaChartPie,
    value: "80",
    suffix: "+",
    label: "Global Clients",
    desc: "Brands, startups, and high-growth businesses",
  },
  {
    icon: FaSmile,
    value: "98",
    suffix: "%",
    label: "Client Satisfaction",
    desc: "Long-term relationships with compounding growth",
  },
  {
    icon: FaAward,
    value: "5",
    suffix: "+",
    label: "Years Experience",
    desc: "Mastery across SEO, full-stack dev & paid ads",
  },
  {
    icon: FaStar,
    value: "4.9",
    suffix: "★",
    label: "Verified Rating",
    desc: "Consistently rated 5 stars across engagements",
  },
  {
    icon: FaClock,
    value: "24",
    suffix: "h",
    prefix: "<",
    label: "Response Guarantee",
    desc: "Fast, transparent communication whenever you need",
  },
];

const StatsSection = () => {
  return (
    <section className="stats-section" aria-label="Key statistics">
      <div className="stats-wrap">
        <motion.div
          className="stats-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="stats-kicker">Data-Driven Results</span>
          <h2>Proven Impact, Measured in Real Numbers</h2>
          <p>
            We prioritize measurable returns, organic visibility, and lead generation over vanity metrics.
          </p>
        </motion.div>

        <div className="stats-grid">
          {statsData.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                className="stat-card"
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
              >
                <div className="stat-icon-wrapper">
                  <Icon />
                </div>
                <div className="stat-value-box">
                  {stat.prefix && <span className="stat-prefix">{stat.prefix}</span>}
                  <CounterNumber target={stat.value} suffix={stat.suffix} />
                </div>
                <h3 className="stat-title">{stat.label}</h3>
                <p className="stat-desc">{stat.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
